import React from 'react';
import { useDispatch } from 'react-redux';
import { Backdrop, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { Form, useIsModuleDisabled, useSettings, useUserData, Utils } from 'app/main/components';
import useModel, { type ModelType } from 'app/main/components/hooks/useModel';

import { calcPricing, getDefaultQuery } from '../components/OrdersLinesTableUtils';

import OrdersLinesEditDialogMainSection from './sections/OrdersLinesEditDialogMainSection';
import OrdersLinesEditDialogPriceSection from './sections/OrdersLinesEditDialogPriceSection';
import OrdersLinesEditDialogExpensesSection from './sections/OrdersLinesEditDialogExpensesSection';
import OrdersLinesEditDialogProjectSection from './sections/OrdersLinesEditDialogProjectSection';

interface OrdersLinesEditDialogProps {
	baseModel: any;
	discountPercentage: number;
	pricingGroupId: string;
	compUid: string;
	isAdd?: boolean;
	updateRefresh: VoidFunction;
}

export interface OrdersLinesEditDialogSection {
	model: any;
	sendChange: ModelType['sendChange'];
	sendMultipleChanges: (changes: any) => void;
	canEdit?: boolean;
	canEditPrice?: boolean;
}

export function showOrdersLinesEditDialog(props: OrdersLinesEditDialogProps) {
	return openDialog({
		children: <OrdersLinesEditDialog {...props} />,
		maxWidth: 'sm',
		fullWidth: true
	});
}

export default function OrdersLinesEditDialog(props: OrdersLinesEditDialogProps) {
	const dispatch = useDispatch();
	const { settings, sysSettings, t } = useSettings('orders_lines');
	const userData = useUserData();
	const isModuleDisabled = useIsModuleDisabled();

	const { model, modifiedModel, setModifiedModel, save } = useModel({
		baseModel: props.baseModel,
		settings,
		isEdit: true,
		isAdd: props.isAdd
	});

	const [isLoading, setIsLoading] = React.useState(false);

	const canEdit = !userData.user_role_external;
	const canEditPrice = canEdit && sysSettings.orders_price_custom_enabled === 'true';

	const isItem = model.orli_type === 'item' || model.orli_item_uid;
	const isWorkReportsHours = model.orli_type === 'workreports' || model.orli_woty_id;

	const areExpensesEnabled = sysSettings.orders_expenses_manager_enabled === 'true';
	const areLinkedProjectsEnabled = sysSettings.orders_lines_projects_enabled === 'true';

	function sendMultipleChanges(changes) {
		const newPricing = calcPricing(changes, props.discountPercentage);
		setModifiedModel(state => ({ ...state, ...newPricing }));
	}

	function sendChange(name: string, newValue: any) {
		sendMultipleChanges({ ...model, [name]: newValue });
	}

	function handleItemChange(newItem: any) {
		if (newItem !== null) {
			const filters = [{ filt_field: 'itpr_item_uid', filt_clause: 'equals', filt_value: newItem.item_uid }];
			const query = getDefaultQuery(props.pricingGroupId, props.compUid);

			setIsLoading(true);
			Utils.executeStored({
				action: 'st_items_pricings_list_active',
				params: { n_page: 1, n_rows: 1, filters, query },
				dispatch,
				successHandler: ({ data }) => {
					const newModel = model;
					newModel.orli_item_uid = newItem.item_uid;
					newModel.orli_description = newItem.item_title;
					newModel.pricings_lots = data.list?.[0].pricings_lots;

					const minQuantity = newItem.item_ords_qty_min || 0;
					if (minQuantity > (model.orli_quantity || 0)) newModel.orli_quantity = minQuantity;

					sendMultipleChanges(newModel);
					setIsLoading(false);
				}
			});
		}
	}

	function saveModel() {
		if (!props.isAdd) {
			save({
				onSuccess: () => {
					dispatch(Utils.alertSuccessMsg(t));
					dispatch(closeDialog());
					props.updateRefresh();
				}
			});
		} else {
			dispatch(Utils.alertProgressMsg(t));
			Utils.executeStored({
				action: 'st_orders_lines_add',
				dispatch,
				data: {
					orli_ords_uid: model.orli_ords_uid,
					orli_user_created_uid: userData.user_uid,
					lines: [modifiedModel]
				},
				successHandler: () => {
					dispatch(Utils.alertSuccessMsg(t));
					dispatch(closeDialog());
					props.updateRefresh();
				}
			});
		}
	}

	return (
		<>
			<DialogTitle>
				{t(props.isAdd ? 'DIALOG_ORDERS_LINES_DIALOG_ADD_TITLE' : 'DIALOG_ORDERS_LINES_DIALOG_EDIT_TITLE')}
			</DialogTitle>
			<DialogContent>
				<Backdrop open={isLoading}>
					<CircularProgress disableShrink />
				</Backdrop>
				<Form id="ordersLinesEditDialog" onSubmit={saveModel}>
					<Grid container spacing={1}>
						<OrdersLinesEditDialogMainSection
							model={model}
							sendChange={sendChange}
							sendMultipleChanges={sendMultipleChanges}
							handleItemChange={handleItemChange}
							canEdit={canEdit}
							isItem={isItem}
							isWorkReportsHours={isWorkReportsHours}
						/>
						{(isItem || isWorkReportsHours) && (
							<>
								<OrdersLinesEditDialogPriceSection
									model={model}
									sendChange={sendChange}
									sendMultipleChanges={sendMultipleChanges}
									canEditPrice={canEditPrice}
								/>
								{areExpensesEnabled && (
									<OrdersLinesEditDialogExpensesSection
										model={model}
										sendChange={sendChange}
										sendMultipleChanges={sendMultipleChanges}
										canEditPrice={canEditPrice}
									/>
								)}
								{areLinkedProjectsEnabled && !isModuleDisabled('projects') && (
									<OrdersLinesEditDialogProjectSection
										model={model}
										sendChange={sendChange}
										sendMultipleChanges={sendMultipleChanges}
										canEdit={canEdit}
									/>
								)}
							</>
						)}
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button type="submit" form="ordersLinesEditDialog" color="secondary">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
